import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { supabase } from '../supabaseClient';
import DashboardSidebarAdmin from '../components/Dashboard/DashboardSidebarAdmin';
import DashboardNavbarAdmin from '../components/Dashboard/DashboardNavbarAdmin';
import { Save, ArrowLeft, Loader2, Image as ImageIcon, ChevronDown, ChevronUp } from 'lucide-react';

const AdminBlogEditorPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;
    const editorRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);
    const [showMeta, setShowMeta] = useState(true); // Toggle for metadata section

    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        status: 'draft',
        meta_title: '',
        meta_description: '',
        featured_image: '',
        keywords: '',
        category_id: ''
    });

    const [categories, setCategories] = useState([]);

    useEffect(() => {
        const fetchContent = async () => {
            const { data } = await supabase.from('blog_categories').select('*').order('name');
            setCategories(data || []);
        };
        fetchContent();
    }, []);

    useEffect(() => {
        if (isEditMode) {
            const fetchBlog = async () => {
                const { data, error } = await supabase.from('blogs').select('*').eq('id', id).single();
                if (error) {
                    alert('Error: ' + error.message);
                    navigate('/dashboardadmin/blog');
                } else {
                    setFormData({ ...data, keywords: data.keywords ? data.keywords.join(', ') : '' });
                }
                setFetching(false);
            };
            fetchBlog();
        }
    }, [id, isEditMode, navigate]);

    // Strict Auto-Slug: Always derived from Title (Read-Only)
    const handleTitleChange = (e) => {
        const title = e.target.value;
        const generatedSlug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
        setFormData(prev => ({
            ...prev,
            title,
            slug: generatedSlug
        }));
    };

    const [isDragging, setIsDragging] = useState(false);

    const processFile = async (file) => {
        if (!file) return;
        const fileName = `blog-${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, '')}`;
        const { error } = await supabase.storage.from('assets').upload(`blog/${fileName}`, file);
        if (error) {
            alert('Upload failed: ' + error.message);
        } else {
            const { data: { publicUrl } } = supabase.storage.from('assets').getPublicUrl(`blog/${fileName}`);
            setFormData(prev => ({ ...prev, featured_image: publicUrl }));
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        processFile(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        processFile(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const payload = {
            ...formData,
            keywords: formData.keywords.split(',').map(k => k.trim()).filter(Boolean),
            content: editorRef.current ? editorRef.current.getContent() : formData.content,
            updated_at: new Date().toISOString()
        };

        const { error } = isEditMode
            ? await supabase.from('blogs').update(payload).eq('id', id)
            : await supabase.from('blogs').insert([payload]);

        if (error) alert('Error: ' + error.message);
        else navigate('/dashboardadmin/blog');

        setLoading(false);
    };

    if (fetching) return <div className="h-screen flex items-center justify-center bg-white">Loading...</div>;

    return (
        <div className="flex h-screen bg-white font-sans text-slate-900 overflow-hidden">
            <DashboardSidebarAdmin />

            <div className="flex-1 flex flex-col min-w-0">
                <DashboardNavbarAdmin displayName={isEditMode ? "Edit Artikel" : "Artikel Baru"} />

                <main className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 bg-slate-50">
                    <form onSubmit={handleSubmit} className="max-w-5xl mx-auto p-6 md:p-10 space-y-6">

                        {/* 1. TOP SECTION: TITLE & MAIN META */}
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

                            {/* TITLE INPUT */}
                            <div className="mb-6">
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Judul Artikel</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full text-3xl font-bold text-slate-900 placeholder:text-slate-300 border-b-2 border-slate-100 focus:border-sky-500 outline-none py-2 bg-transparent transition-colors"
                                    placeholder="Ketik judul artikel di sini..."
                                    value={formData.title}
                                    onChange={handleTitleChange}
                                />
                            </div>

                            {/* SLUG (READ ONLY) */}
                            <div className="mb-6 bg-slate-50 p-3 rounded-lg border border-slate-200 flex items-center gap-3">
                                <span className="text-xs font-bold text-slate-400 uppercase shrink-0">Permalink:</span>
                                <code className="text-sm text-sky-600 font-medium flex-1 truncate">
                                    neoai.id/blog/{formData.slug || '...'}
                                </code>
                                <span className="text-[10px] text-slate-400 bg-white px-2 py-1 rounded border border-slate-100 uppercase font-bold">Auto-Generated</span>
                            </div>

                            {/* META CONTROLS (Collapsible) */}
                            <div>
                                <button
                                    type="button"
                                    onClick={() => setShowMeta(!showMeta)}
                                    className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-sky-600 transition-colors mb-4"
                                >
                                    {showMeta ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    {showMeta ? 'Sembunyikan Metadata' : 'Tampilkan Metadata (Kategori, SEO, Cover)'}
                                </button>

                                {showMeta && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-top-2 duration-200">

                                        {/* LEFT META: Category & Cover */}
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Kategori</label>
                                                <select
                                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-sky-500/20 outline-none"
                                                    value={formData.category_id || ""}
                                                    onChange={e => setFormData({ ...formData, category_id: e.target.value })}
                                                >
                                                    <option value="">-- Pilih Kategori --</option>
                                                    {categories.map(cat => (
                                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Cover Image (Drag & Drop)</label>
                                                <div
                                                    className={`flex items-center gap-4 p-2 rounded-lg border-2 border-dashed transition-all ${isDragging ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-sky-300'}`}
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    onDrop={handleDrop}
                                                >
                                                    <div className="w-20 h-20 bg-slate-100 rounded-lg overflow-hidden border border-slate-200 shrink-0 relative">
                                                        {formData.featured_image ? (
                                                            <img src={formData.featured_image} alt="Preview" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-slate-300"><ImageIcon size={20} /></div>
                                                        )}
                                                    </div>
                                                    <div className="flex-1 relative">
                                                        <p className="text-xs font-semibold text-slate-700 mb-1">
                                                            {isDragging ? 'Lepaskan gambar di sini' : 'Klik atau Tarik Gambar'}
                                                        </p>
                                                        <p className="text-[10px] text-slate-400">JPG, PNG, WebP (Max 2MB)</p>
                                                        <input
                                                            type="file"
                                                            className="absolute inset-0 opacity-0 cursor-pointer"
                                                            accept="image/*"
                                                            onChange={handleImageUpload}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* RIGHT META: SEO */}
                                        <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-100">
                                            <h4 className="text-xs font-bold text-slate-900 uppercase">SEO Configuration</h4>
                                            <input
                                                type="text"
                                                placeholder="Meta Title (Opsional)"
                                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-xs focus:border-sky-500 outline-none"
                                                value={formData.meta_title}
                                                onChange={e => setFormData({ ...formData, meta_title: e.target.value })}
                                            />
                                            <textarea
                                                rows={2}
                                                placeholder="Meta Description (Opsional)"
                                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-xs focus:border-sky-500 outline-none resize-none"
                                                value={formData.meta_description}
                                                onChange={e => setFormData({ ...formData, meta_description: e.target.value })}
                                            />
                                            <input
                                                type="text"
                                                placeholder="Keywords (koma, pisahkan)"
                                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-xs focus:border-sky-500 outline-none"
                                                value={formData.keywords}
                                                onChange={e => setFormData({ ...formData, keywords: e.target.value })}
                                            />
                                        </div>

                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 2. BOTTOM SECTION: EDITOR */}
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col min-h-[500px]">
                            <Editor
                                apiKey='axra7dpv64218ujg6j8ptnal1gesrc1gxi3kd8160l95gb9t'
                                onInit={(evt, editor) => editorRef.current = editor}
                                initialValue={formData.content}
                                init={{
                                    height: 500,
                                    menubar: true, // Show menubar as requested "File Edit View..."
                                    plugins: [
                                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                        'media', 'table', 'code', 'help', 'wordcount'
                                    ],
                                    toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright | bullist numlist outdent indent | link image media | code',
                                    content_style: `
                                        body { font-family: 'Inter', sans-serif; font-size: 16px; line-height: 1.6; color: #334155; margin: 2rem; }
                                        img { max-width: 100%; height: auto; border-radius: 4px; }
                                    `,
                                }}
                            />
                        </div>

                        {/* 3. STATIC BOTTOM ACTIONS */}
                        <div className="flex flex-col sm:flex-row gap-4 mt-6">
                            {/* STATUS SELECTOR (Dynamic Colors) */}
                            <div className="relative sm:w-1/4">
                                <select
                                    className={`w-full appearance-none px-6 py-3 border rounded-lg font-bold text-center text-sm uppercase tracking-wide cursor-pointer focus:outline-none focus:ring-2 transition-colors
                                        ${formData.status === 'published' ? 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200 focus:ring-green-400' :
                                            formData.status === 'archived' ? 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200 focus:ring-red-400' :
                                                'bg-yellow-100 text-yellow-700 border-yellow-300 hover:bg-yellow-200 focus:ring-yellow-400'}`}
                                    value={formData.status}
                                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                                >
                                    <option value="draft" className="bg-yellow-100 text-yellow-900">Draft</option>
                                    <option value="published" className="bg-green-100 text-green-900">Published</option>
                                    <option value="archived" className="bg-red-100 text-red-900">Archived</option>
                                </select>
                                <ChevronDown size={14} className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none 
                                    ${formData.status === 'published' ? 'text-green-700' :
                                        formData.status === 'archived' ? 'text-red-700' :
                                            'text-yellow-700'}`}
                                />
                            </div>

                            {/* SAVE BUTTON (Styled as Blue Button) */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-6 py-3 bg-[#027BFF] text-white border border-blue-600 rounded-lg text-sm font-bold uppercase tracking-wide hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                            >
                                {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                                Simpan
                            </button>
                        </div>

                    </form>
                </main>
            </div>
        </div>
    );
};

export default AdminBlogEditorPage;
